<template>
  <div class="search-params-mobile py-4 px-6" :class="{ 'z-30': active }">
    <div class="search-value w-full flex items-center" @click="openSearchFields">
      <div class="fs-14 mdsm:text-center">
        {{ searchDisplay }}
      </div>
      <svg width="24" height="24" class="ml-3 flex-none fill-secondary">
        <use xlink:href="/img/sprite.svg#ic_note-edit"></use>
      </svg>
    </div>

    <transition name="fadedown">
      <div class="search-fields bg-white p-6" v-if="active">
        <div class="search-fields-header flex item-center justify-center mb-6 relative">
          <div class="heading-text fs-14">
            Edit your search
          </div>

          <div class="search-close-icon" @click="closeSearchFields">
            <svg width="24" height="24">
              <use xlink:href="/img/sprite.svg#ic_close--solid"></use>
            </svg>
          </div>
        </div>

        <div class="search-fields-form">
          <div class="mt-4 relative">
            <free-text-search
              ref="fts"
              v-model="entry.title"
              :search-type="searchType"
              @confirmed="handleDestinationConfirmed"
              @hide="isDismissable = true"
              @show="isDismissable = false" />
          </div>

          <div class="mt-4">
            <product-finder
              ref="productFinder"
              class="bg-white relative"
              v-model="searchType" />
          </div>

          <div class="mt-4">
            <date-picker-mobile
              class="mt-4"
              v-model="dates"
              :search-type="searchType"
              @hide="isDismissable = true"
              @show="isDismissable = false" />
          </div>

          <div class="mt-4">
            <guest-picker
              v-model="guests"
              :search-type="searchType"
              @hide="isDismissable = true"
              @show="isDismissable = false" />
          </div>

          <div class="mt-6">
            <button
              class="search-cta w-full flex items-center justify-center font-light rounded-full py-3 border border-secondary text-secondary"
              @click="handleCTA"
              :disabled="isWorking"
            >
              Search
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ProductFinder from './2021/ProductFinder'
import FreeTextSearch from './2021/FreeTextSearch'
import DatePickerMobile from './2021/DatePickerMobile'
import GuestPicker from './2021/GuestPicker'
import WidgetMixin from '../Search/2021/WidgetMixin'
import {
  getBookingDateDisplay
} from '../../helpers'

export default {
  mixins: [WidgetMixin],
  components: {
    ProductFinder,
    FreeTextSearch,
    DatePickerMobile,
    GuestPicker
  },
  data() {
    const searchParams = new URLSearchParams(window.location.search)
    let dates = []
    let guests = '0'

    if (searchParams.has('from')) {
      const from = moment(searchParams.get('from'), 'YYYY-MM-DD', true)
      const to = moment(searchParams.get('to'), 'YYYY-MM-DD', true)

      if (from.isValid()) {
        dates.push(from)
      }
      if (to.isValid()) {
        dates.push(to)
      }
    }

    if (searchParams.has('guests')) {
      guests = searchParams.get('guests')
    }

    return {
      searchType: window.zubluSearchType,
      active: false,
      isDismissable: true,
      entry: {
        title: window.zubluDestination ? window.zubluDestination.name : ''
      },
      dates,
      guests,
      isWorking: false,
      ecoventureDestinationId: null
    }
  },
  mounted () {
    if (this.searchType === 'ecoventure') {
      const searchParams = new URLSearchParams(window.location.search)
      const destinationIds = searchParams.get('destinations') || ''
      const firstDestination = destinationIds.split(',')[0]

      if (firstDestination) {
        $.ajax({
          method: 'POST',
          url: '/',
          data: {
            destination_id: firstDestination,
            action: 'zuBlu/public/getDestinationStructureByCode',
            [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
          },
          headers: {'Content-Type': 'application/x-www-form-urlencoded'}
        })
        .then(response => {
          try {
            this.entry.title = JSON.parse(response).name
            this.ecoventureDestinationId = JSON.parse(response).id;
          } catch (error) {}
        })
      }
    }
  },
  computed: {
    searchTypeDisplay () {
      switch (this.searchType) {
        case 'resort': return 'Resorts'
        case 'liveaboard': return 'Liveaboards'
        case 'ecoventure': return 'Ecoventures'
        case 'yacht': return 'Yachts'
        default: return null
      }
    },
    searchDisplay () {
      return [
        this.entry.title,
        this.searchTypeDisplay,
        getBookingDateDisplay(this.dates[0], this.dates[1]).join(' — ')
      ]
      .filter(part => part)
      .join(' · ')
    }
  },
  methods: {
    openSearchFields() {
      this.active = true
    },
    closeSearchFields() {
      this.active = false
    },
    handleDestinationConfirmed (which) {
      this.handleConfirmed(which);
      this.$refs.productFinder.fetchProductAvailability(which.id);
    }
  },
  watch: {
    active (value) {
      if (value) {
        this.$nextTick(() => {
          if (window.zubluDestination) {
            this.$refs.productFinder.fetchProductAvailability(window.zubluDestination.id);
          }
          else if (this.ecoventureDestinationId) {
            this.$refs.productFinder.fetchProductAvailability(this.ecoventureDestinationId);
          }
        })
      }
    }
  },
}
</script>

<style scoped lang="scss">
.search-params-mobile ::v-deep {
  position: relative;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.16);
  z-index: 11;

  .search-input {
    border: 1px solid var(--black-light-2);
  }
}

.search-fields {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.16);

  &:before {
    content: '';
    position: absolute;
    width: 64px;
    height: 64px;
    right: 0;
    top: -64px;
    background: white;
  }
}

.search-value {
  background: var(--black-light-1);
  padding: 0.5rem 1rem;
  border-radius: 100vh;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-close-icon {
  position: absolute;
  right: -8px;
  z-index: 1;
}

.search-close-icon::after {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: var(--black-light-1);
  z-index: -1;
  border-radius: 5rem;
}

.search-fields-form ::v-deep .search-input,
.search-fields-form ::v-deep .product-dropdown-trigger {
  border: 1px solid var(--border-primary);
}

.search-cta {
  transition: ease .25s;
}
</style>
